import React, { useMemo, useRef, useState } from 'react';

import { ApolloError } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { Search } from '@modernloop/shared/components';
import { FilterIcon } from '@modernloop/shared/icons';
import { Box, Paper, Popover, Stack, Tab, Tabs } from '@mui/material';

import { AttributesFilterFragment, EmployeeFilterFragment } from 'src/generated/mloop-graphql';

import FilterButton from 'src/components/FilterButton';

import { BaseAttributesFilter, EmployeeFilter } from 'src/entities/InterviewPlan/InterviewSeat/ModuleMemberFilters';

import { AttributeMap, JobStageInterviewSeat } from 'src/store/slices/job-stage-interview-seat';

import { ModuleFilterRole, SOURCE_MODE, SourceMode } from './seatSettingsTypes';
import { countAttributeValues } from './seatSettingsUtils';

const FILTER_POPOVER_WIDTH = 360;
const PEOPLE_LIST_HEIGHT = 320;

type Props = {
  attributeData: AttributesFilterFragment[];
  attributeMap?: AttributeMap;
  attributesError?: ApolloError;
  attributesLoading: boolean;
  disabled?: boolean;
  interviewModuleMembers: EmployeeFilterFragment[];
  onAttributeUpdate: (nameId: string, valueId: string, selected: boolean) => void;
  onSourceModeChange: (mode: SourceMode) => void;
  onUpdated: (seat: JobStageInterviewSeat) => void;
  role: ModuleFilterRole;
  seat: JobStageInterviewSeat;
  selectedPeopleCount: number;
  sourceMode: SourceMode;
};

/**
 * Filter button with a people/attributes tabbed popover for a single module seat role.
 */
const ModuleFilterDropdownButton = ({
  attributeData,
  attributeMap,
  attributesError,
  attributesLoading,
  disabled = false,
  interviewModuleMembers,
  onAttributeUpdate,
  onSourceModeChange,
  onUpdated,
  role,
  seat,
  selectedPeopleCount,
  sourceMode,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [filterText, setFilterText] = useState('');

  const attributeCount = countAttributeValues(attributeMap);
  const hasSelections =
    (sourceMode === SOURCE_MODE.PEOPLE && selectedPeopleCount > 0) ||
    (sourceMode === SOURCE_MODE.ATTRIBUTES && attributeCount > 0);

  const buttonLabel = useMemo(() => {
    if (sourceMode === SOURCE_MODE.ATTRIBUTES && attributeCount > 0) {
      return t('hiring-dashboard-filter:filter.attributesFilterDetail', { count: attributeCount });
    }
    if (sourceMode === SOURCE_MODE.PEOPLE && selectedPeopleCount > 0) {
      return t('hiring-dashboard-filter:filter.peopleFilterDetail', { count: selectedPeopleCount });
    }
    return t('hiring-dashboard-filter:filter.seatFilter');
  }, [attributeCount, selectedPeopleCount, sourceMode, t]);

  const searchPlaceholder =
    sourceMode === SOURCE_MODE.PEOPLE
      ? t('hiring-dashboard-filter:filter.searchInterviewers')
      : t('hiring-dashboard-filter:filter.searchAttributes');

  const displayedInterviewModuleMembers = useMemo(() => {
    if (sourceMode !== SOURCE_MODE.PEOPLE || !filterText.trim()) {
      return interviewModuleMembers;
    }

    const normalizedFilterText = filterText.trim().toLocaleLowerCase();
    return interviewModuleMembers.filter((member) =>
      [member.employee?.fullName, member.employee?.email, member.employee?.title].some((value) =>
        value?.toLocaleLowerCase().includes(normalizedFilterText)
      )
    );
  }, [filterText, interviewModuleMembers, sourceMode]);

  const handleOpen = () => {
    if (disabled) {
      return;
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFilterText('');
  };

  const handleTabChange = (nextMode: SourceMode) => {
    if (nextMode === sourceMode) {
      return;
    }
    setFilterText('');
    onSourceModeChange(nextMode);
  };

  const tabs = [
    {
      value: SOURCE_MODE.PEOPLE,
      label: t('hiring-dashboard-filter:filter.tabPeople'),
    },
    {
      value: SOURCE_MODE.ATTRIBUTES,
      label: t('hiring-dashboard-filter:filter.attributes'),
    },
  ];

  return (
    <>
      <FilterButton
        ref={buttonRef}
        hasSelections={hasSelections}
        onClick={handleOpen}
        dataTestId={`module-filter-button-${role}`}
        disabled={disabled}
        startIcon={<FilterIcon color="inherit" fontSize="small" />}
      >
        {buttonLabel}
      </FilterButton>
      <Popover
        open={open && !disabled}
        anchorEl={buttonRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        data-testid={`module-filter-popover-${role}`}
      >
        <Paper
          sx={{
            width: FILTER_POPOVER_WIDTH,
            maxWidth: `calc(100vw - 32px)`,
            overflow: 'hidden',
            padding: 1.5,
          }}
        >
          <Stack spacing={1}>
            <Search autoFocus fullWidth placeholder={searchPlaceholder} value={filterText} onChange={setFilterText} />
            <Tabs value={sourceMode} onChange={(_, value) => handleTabChange(value as SourceMode)}>
              {tabs.map((tab) => (
                <Tab key={tab.value} value={tab.value} label={tab.label} />
              ))}
            </Tabs>
            {sourceMode === SOURCE_MODE.PEOPLE && (
              <Box sx={{ height: PEOPLE_LIST_HEIGHT, overflowY: 'auto' }}>
                <EmployeeFilter
                  interviewModuleMembers={displayedInterviewModuleMembers}
                  seat={seat}
                  onUpdated={onUpdated}
                />
              </Box>
            )}
            {sourceMode === SOURCE_MODE.ATTRIBUTES && (
              <BaseAttributesFilter
                loading={attributesLoading && attributeData.length === 0}
                filterText={filterText}
                error={attributesError}
                data={attributeData}
                attributeMap={attributeMap}
                onAttributeUpdate={onAttributeUpdate}
              />
            )}
          </Stack>
        </Paper>
      </Popover>
    </>
  );
};

export default ModuleFilterDropdownButton;
